import { useMemo } from "react";
import { HiExclamation } from "react-icons/hi";
import { IoClose } from "react-icons/io5";

import PrescriptionViewer from "../PrescriptionViewer";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString("fr-FR") : "—");

const doctorName = (doctor) => {
  if (!doctor?.lastName && !doctor?.firstName?.length) return "—";
  const first = Array.isArray(doctor.firstName) ? doctor.firstName.join(" ") : doctor.firstName;
  return `Dr ${[first, doctor.lastName].filter(Boolean).join(" ")}`;
};

const getCodes = (acts) => [...new Set((acts || []).flatMap((act) => act.codes || []))];

const RenewalComparisonModal = ({ prescription, renewal, onClose, onConfirm, loading }) => {
  const currentCodes = useMemo(() => getCodes(prescription.acts), [prescription.acts]);
  const renewalCodes = useMemo(() => getCodes(renewal.acts), [renewal.acts]);
  const expired = renewal.expiresAt && new Date(renewal.expiresAt) < new Date();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4">
      <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-medium text-gray-900">Comparer le renouvellement</h2>
            <p className="mt-0.5 text-[13px] text-gray-500">{"Vérifiez les informations avant de remplacer l'ordonnance actuelle."}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600">
            <IoClose className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 items-start">
            {/* Header row */}
            <div className="flex items-center border-b border-r border-gray-100 bg-white px-4 py-2.5">
              <h3 className="section-title">Ordonnance actuelle</h3>
            </div>
            <div className="flex items-center gap-3 border-b border-gray-100 bg-white px-4 py-2.5">
              <h3 className="section-title">Renouvellement</h3>
              <div className="ml-auto flex items-center gap-3 text-xs">
                {renewal.expiresAt && (
                  <span className={expired ? "text-red-600" : "text-gray-500"}>
                    {expired ? "Expiré le" : "Expire le"} {formatDate(renewal.expiresAt)}
                  </span>
                )}
                {renewal.nbRequested != null && (
                  <span className="chip border-gray-200 bg-gray-50 text-gray-600" title="Renouvellements réalisés / demandés">
                    {renewal.nbMade ?? 0} / {renewal.nbRequested}
                  </span>
                )}
              </div>
            </div>

            {/* Image row */}
            <div className="border-r border-gray-100 bg-white px-4 py-4">
              <div className="overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
                {prescription.img ? (
                  <PrescriptionViewer id={`compare-current-${prescription._id}`} imgUrl={prescription.img} heightRatio={0.6} />
                ) : (
                  <div className="flex h-40 items-center justify-center text-sm text-gray-400">Aucune image disponible</div>
                )}
              </div>
            </div>
            <div className="bg-white px-4 py-4">
              <div className="overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
                {renewal.s3Url ? (
                  <PrescriptionViewer id={`compare-renewal-${renewal.key}`} imgUrl={renewal.s3Url} heightRatio={0.6} />
                ) : (
                  <div className="flex h-40 items-center justify-center text-sm text-gray-400">Aucune image disponible</div>
                )}
              </div>
            </div>

            {/* Prescription / Médecin row */}
            <div className="border-r border-gray-100 bg-white px-4 pb-4">
              <div className="flex items-center gap-2 py-1">
                <span className="field-label w-[96px] shrink-0">Prescription</span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{formatDate(prescription.date)}</span>
              </div>
              <div className="flex items-center gap-2 py-1">
                <span className="field-label w-[96px] shrink-0">Médecin</span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{doctorName(prescription.doctor)}</span>
              </div>
            </div>
            <div className="bg-white px-4 pb-4">
              <div className="flex items-center gap-2 py-1">
                <span className="field-label w-[96px] shrink-0">Prescription</span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{formatDate(renewal.date)}</span>
              </div>
              <div className="flex items-center gap-2 py-1">
                <span className="field-label w-[96px] shrink-0">Médecin</span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{doctorName(renewal.doctor)}</span>
              </div>
            </div>

            {/* Analyses row */}
            <div className="border-r border-gray-100 bg-white px-4 pb-4">
              <div className="field-label mb-1.5">Analyses</div>
              {currentCodes.length ? (
                <div className="flex flex-wrap gap-1">
                  {currentCodes.map((code) => (
                    <span key={code} className="chip border-gray-200 bg-gray-50 text-gray-600">
                      {code}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-sm text-gray-400">Aucune analyse</span>
              )}
            </div>
            <div className="bg-white px-4 pb-4">
              <div className="field-label mb-1.5">Analyses</div>
              {renewalCodes.length ? (
                <div className="flex flex-wrap gap-1">
                  {renewalCodes.map((code) => (
                    <span key={code} className="chip border-gray-200 bg-gray-50 text-gray-600">
                      {code}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-sm text-gray-400">Aucune analyse</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
            <HiExclamation className="h-4 w-4 text-orange-500" />
            Cette action est irréversible.
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-primary-500 transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}
              {loading ? "Remplacement…" : "Remplacer par le renouvellement"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalComparisonModal;
